import { createFileRoute } from '@tanstack/react-router';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { Button, Chip, Divider, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { registerCompanyDocument } from '@social-garden/api/documents/company.ts';
import {
  RegisterCompanyFieldValues,
  RegisterCompanySchema,
} from '@/constants/ValidationSchema.ts';
import CompanyNameField from '@/components/CompanyNameField.tsx';
import EmailField from '@/components/EmailField.tsx';
import ConfirmPasswordField from '@/components/ConfirmPasswordField.tsx';
import NewPasswordField from '@/components/NewPasswordField.tsx';
import TermsOfServiceField from '@/components/TermsOfServiceField.tsx';
import PrivacyPolicyField from '@/components/PrivacyPolicyField.tsx';
import NewsletterField from '@/components/NewsletterField.tsx';
import Link from '@/components/Link.tsx';
import usePreventLeavingUnsavedForm from '@/hooks/usePreventLeavingUnsavedForm.ts';
import PageTitle from '@/components/PageTitle.tsx';

export const Route = createFileRoute('/_guest/register')({
  component: Register,
});

function Register() {
  const {
    t,
    i18n: { resolvedLanguage, languages },
  } = useTranslation(['manager']);
  const navigate = useNavigate();

  const [registerCompany, { loading }] = useMutation(registerCompanyDocument);

  const {
    control,
    formState: { dirtyFields, isValid, isSubmitting },
    handleSubmit,
  } = useForm<RegisterCompanyFieldValues>({
    resolver: zodResolver(RegisterCompanySchema),
    mode: 'all',
    defaultValues: {
      acceptedNewsletter: false,
    },
  });

  usePreventLeavingUnsavedForm({ dirtyFields, isSubmitting });

  const handleOnValid = useCallback(
    async ({
      name,
      email,
      password,
      confirmedTermsOfService,
      confirmedPrivacyTerms,
      acceptedNewsletter,
    }: RegisterCompanyFieldValues) => {
      const result = await registerCompany({
        variables: {
          input: {
            name,
            manager: {
              email,
              password,
              confirmedTermsOfService,
              confirmedPrivacyTerms,
              acceptedNewsletter,
              locale: resolvedLanguage ?? languages[0],
            },
          },
        },
      });
      if (result.data?.registerCompany) {
        await navigate({
          to: '/verify-email',
          search: {
            email,
          },
          state: {
            password,
          } as any,
        });
      }
    },
    [registerCompany, navigate, resolvedLanguage, languages],
  );

  return (
    <Stack spacing={4}>
      <Stack spacing={1} alignItems="center">
        <PageTitle variant="h4" textAlign="center">
          {t('register.header')}
        </PageTitle>
      </Stack>
      <Stack
        component="form"
        spacing={2}
        noValidate
        onSubmit={handleSubmit(handleOnValid)}>
        <Divider>
          <Chip label={t('register.companyHeader')} variant="outlined" />
        </Divider>
        <Controller<RegisterCompanyFieldValues, 'name'>
          name="name"
          control={control}
          render={CompanyNameField}
        />
        <Divider>
          <Chip label={t('register.managerHeader')} variant="outlined" />
        </Divider>
        <Controller<RegisterCompanyFieldValues, 'email'>
          name="email"
          control={control}
          render={EmailField}
        />
        <Controller<RegisterCompanyFieldValues, 'password'>
          name="password"
          control={control}
          render={NewPasswordField}
        />
        <Controller<RegisterCompanyFieldValues, 'passwordConfirm'>
          name="passwordConfirm"
          control={control}
          render={ConfirmPasswordField}
        />
        <Controller<RegisterCompanyFieldValues, 'confirmedPrivacyTerms'>
          name="confirmedPrivacyTerms"
          control={control}
          render={PrivacyPolicyField}
        />
        <Controller<RegisterCompanyFieldValues, 'confirmedTermsOfService'>
          name="confirmedTermsOfService"
          control={control}
          render={TermsOfServiceField}
        />
        <Controller<RegisterCompanyFieldValues, 'acceptedNewsletter'>
          name="acceptedNewsletter"
          control={control}
          render={NewsletterField}
        />
        <Button
          fullWidth
          type="submit"
          variant="contained"
          loading={loading}
          disabled={!isValid}>
          {t('register.submit')}
        </Button>
        <Link to="/login">{t('register.loginLink')}</Link>
      </Stack>
    </Stack>
  );
}
